/**
 * useRecordContext (13-27): a few read-only context columns of a record, read only when they are asked for (the
 * reportability panel's "The agent's draft, as written", opened by the decider). A live record is read with one short
 * item GET (`recordService.getRecordColumns`, never added to the record read, ISS-261); a practice record already
 * holds its fields. A failed read is said, never shown as blank.
 */

import { useEffect, useState } from 'react';
import { useActionRuntime } from './useActionRuntime';
import { isLiveRef, isPracticeRef } from '../lib/recordQuery';
import { getRecordColumns } from '../services/recordService';
import type { RecordIO } from '../services/recordService';
import type { RecordRow } from '../types/records';

export type RecordContextState =
  | { status: 'idle' | 'loading'; fields: Readonly<Record<string, unknown>> }
  | { status: 'ready'; fields: Readonly<Record<string, unknown>> }
  | { status: 'error'; fields: Readonly<Record<string, unknown>>; error: string };

const NONE: Readonly<Record<string, unknown>> = {};

export function useRecordContext(row: RecordRow, columns: readonly string[], enabled: boolean): RecordContextState {
  const { io } = useActionRuntime();
  const practice = isPracticeRef(row.ref);
  const live = isLiveRef(row.ref);
  const [result, setResult] = useState<{ ref: string; fields?: Record<string, unknown>; error?: string } | null>(null);
  const have = result !== null && result.ref === row.ref;

  useEffect(() => {
    if (!enabled || practice || !live || !io || have) return;
    let current = true;
    getRecordColumns(io as RecordIO, row.ref, columns)
      .then((fields) => current && setResult({ ref: row.ref, fields }))
      .catch((err: unknown) => current && setResult({ ref: row.ref, error: err instanceof Error ? err.message : 'It could not be read.' }));
    return () => {
      current = false;
    };
  }, [enabled, practice, live, io, have, row.ref, columns]);

  if (practice) return { status: 'ready', fields: row.fields };
  if (!enabled) return { status: 'idle', fields: NONE };
  if (!have || !result) return { status: 'loading', fields: NONE };
  return result.error !== undefined ? { status: 'error', fields: NONE, error: result.error } : { status: 'ready', fields: result.fields ?? NONE };
}
